import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { usePublicAnalytics } from "@/hooks/usePublicAnalytics";

export function PricingBand() {
  const { t } = useTranslation();
  const { track } = usePublicAnalytics();
  const freeOutcomes = ["logging", "templates", "coach"] as const;
  const premiumOutcomes = ["history", "planning", "coach"] as const;

  return (
    <section id="pricing" className="bg-background px-4 py-20 md:px-8 md:py-28">
      <div className="mx-auto max-w-[1280px]">
        <p className="text-sm font-semibold uppercase text-muted">
          {t("frontpage.precision.pricing.eyebrow")}
        </p>
        <h2 className="mt-2 max-w-[22ch] font-display text-[36px] font-bold leading-[1.05] md:text-[52px]">
          {t("frontpage.precision.pricing.title")}
        </h2>
        <p className="mt-5 max-w-[62ch] text-base leading-7 text-muted">
          {t("frontpage.precision.pricing.body")}
        </p>

        <div className="mt-10 grid border border-line md:grid-cols-2">
          <div className="bg-card p-6 md:p-8">
            <h3 className="text-xl font-semibold text-text">
              {t("frontpage.precision.pricing.free_name")}
            </h3>
            <p className="mt-4 font-display text-5xl font-bold text-text">
              {t("frontpage.precision.pricing.free_price")}
            </p>
            <p className="mt-2 text-sm text-muted">
              {t("frontpage.precision.pricing.free_caption")}
            </p>
            <ul className="mt-6 space-y-3">
              {freeOutcomes.map((outcome) => (
                <li key={outcome} className="flex items-start gap-3 text-sm leading-6 text-muted">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-success" aria-hidden />
                  {t(`frontpage.precision.pricing.free_outcomes.${outcome}`)}
                </li>
              ))}
            </ul>
            <Link
              to={ROUTES.SIGNUP}
              onClick={() => void track("pricing_selected", "free")}
              className="mt-8 flex min-h-11 items-center justify-center gap-2 border border-foreground bg-[#171918] px-5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#292d29] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {t("frontpage.precision.nav.signup")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>

          <div className="border-t border-[#353a35] bg-[#171918] p-6 text-white md:border-l md:border-t-0 md:p-8">
            <h3 className="text-xl font-semibold text-white">
              {t("frontpage.precision.pricing.premium_name")}
            </h3>
            <div className="mt-4 flex flex-wrap items-end gap-2">
              <span className="font-display text-5xl font-bold text-[hsl(var(--brand-on-dark))]">
                {t("frontpage.precision.pricing.premium_price")}
              </span>
              <span className="pb-1 text-sm text-white/65">
                {t("frontpage.precision.pricing.premium_period")}
              </span>
            </div>
            <p className="mt-2 text-sm leading-6 text-white/65">
              {t("frontpage.precision.pricing.premium_caption")}
            </p>
            <ul className="mt-6 space-y-3">
              {premiumOutcomes.map((outcome) => (
                <li key={outcome} className="flex items-start gap-3 text-sm leading-6 text-white/75">
                  <Check
                    className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--brand-on-dark))]"
                    aria-hidden
                  />
                  {t(`frontpage.precision.pricing.premium_outcomes.${outcome}`)}
                </li>
              ))}
            </ul>
            <p className="mt-6 border-t border-white/15 pt-4 text-sm leading-6 text-white/65">
              {t("frontpage.precision.pricing.premium_trial")}
            </p>
            <Link
              to={ROUTES.SIGNUP}
              onClick={() => void track("pricing_selected", "premium_monthly")}
              className="mt-8 flex min-h-11 items-center justify-center gap-2 border border-brand bg-brand px-5 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#171918]"
            >
              {t("frontpage.precision.pricing.cta")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>

        <div className="grid border-x border-b border-line bg-muted-bg px-6 py-5 text-sm text-muted md:grid-cols-2 md:gap-8 md:px-8">
          <p>{t("frontpage.precision.pricing.annual")}</p>
          <p className="mt-2 md:mt-0">{t("frontpage.precision.pricing.lifetime")}</p>
        </div>
      </div>
    </section>
  );
}
